import React from "react";
import { useLocation } from "react-router-dom";
import { checkChain } from "../../utils/config";
import useConfig from "../../customHooks/useConfig";
import { CiWarning } from 'react-icons/ci';
import styles from "./ChainSupportCloak.module.sass";

const ChainSupportCloak = ({ children }) => {
    const config = useConfig();
    const location = useLocation();

    const { chainId, chainName } = config;
    const { pathname } = location;

    // Check if current chain supports this page
    const chainConfig = checkChain(chainId, pathname);
    const isChainSupported = chainConfig.chainId !== null && !chainConfig.isDisabled;
    
    // Don't show cloak if no chain is selected or if chain is supported
    if (!chainId || isChainSupported) {
        return children;
    }

    // Don't show cloak on pages that don't require chain support
    const pagesWithoutChainSupport = [
        '/sign-in', 
        '/sign-up', 
        '/settings', 
        '/profile',
        '/notification',
        '/message-center',
        '/explore-contributors',
        '/affiliate-center',
        '/upgrade-to-pro',
        '/questions',
        '/icon-list',
        '/kado'
    ];
    if (pagesWithoutChainSupport.includes(pathname)) {
        return children;
    }

    // Get available chains for this page
    const getAvailableChains = () => {
        const allChains = [
            {
                chainId: 1,
                chainName: "Ethereum",
                chainNetwork: "eth",
                chainIcon: "/images/networks/ethereum.svg",
            },
            {
                chainId: 42161,
                chainName: "Arbitrum",
                chainNetwork: "arbitrum",
                chainIcon: "/images/networks/arbitrum.svg",
            },
            {
                chainId: "casper-test",
                chainName: "Casper",
                chainNetwork: "casper",
                chainIcon: "/images/casper.png",
            },
        ];

        return allChains.filter(chain => {
            const chainSupport = checkChain(chain.chainId, pathname);
            return chainSupport.chainId !== null && !chainSupport.isDisabled;
        });
    };

    const availableChains = getAvailableChains();

    const getPageName = () => {
        switch (pathname) {
            case "/liquid-staking":
                return "Liquid Staking";
            case "/RWAvaults":
                return "RWA Vaults";
            case "/farming":
                return "Power Farms";
            case "/":
                return "the Home page";
            case "/details":
                return "the Overview page";
            case "/liquidations":
                return "Liquidations";
            case "/withdrawals":
                return "Withdrawals";
            case "/statements":
                return "Statements";
            case "/promote":
                return "LiquidNFTs";
            case "/customers/overview":
                return "Analytics";
            case "/customers/customer-list":
                return "Customer List";
            case "/products/add":
                return "New Product";
            case "/products/drafts":
                return "Drafts";
            case "/products/released":
                return "Released";
            case "/products/comments":
                return "Comments";
            case "/products/scheduled":
                return "Scheduled";
            default:
                return "this page";
        }
    };

    const pageName = getPageName();

    return (
        <div className={`${styles.container} ${pathname === '/liquid-staking' ? styles.centered : ''}`}>
            <div className={styles.content}>
                <div className={styles.icon}>
                    <CiWarning size="48" />
                </div>
                <h2 className={styles.title}>
                    Chain Not Supported
                </h2>
                <p className={styles.description}>
                    {chainName || 'This network'} doesn't support {pageName}. Please switch to a supported network.
                </p>
                
                {availableChains.length > 0 && (
                    <div className={styles.supportedChains}>
                        <h3 className={styles.supportedTitle}>
                            Supported Networks:
                        </h3>
                        <div className={styles.chainList}>
                            {availableChains.map((chain) => (
                                <div key={chain.chainId} className={styles.chainItem}>
                                    <img
                                        src={chain.chainIcon}
                                        alt={chain.chainName}
                                        className={styles.chainIcon}
                                        width={24}
                                        height={24}
                                    />
                                    <span className={styles.chainName}>
                                        {chain.chainName}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                <div className={styles.actions}>
                    <button 
                        className={styles.switchButton}
                        onClick={() => {
                            // Trigger network switch modal
                            const event = new CustomEvent('openNetworkModal');
                            window.dispatchEvent(event);
                        }}
                    >
                        Switch Network
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChainSupportCloak; 